import React, { useState, useEffect } from 'react';
import { X, Key, Eye, EyeOff, CheckCircle, AlertCircle, ExternalLink, Save, Trash2 } from 'lucide-react';
import { saveApiToken, getApiToken, clearApiToken, validateApiToken, hasApiToken } from '../utils/tokenStorage';

interface SettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

const Settings: React.FC<SettingsProps> = ({ isOpen, onClose }) => {
  const [apiToken, setApiToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [validationMessage, setValidationMessage] = useState<string>('');
  const [hasToken, setHasToken] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const token = getApiToken();
      if (token) {
        setApiToken(token);
        setHasToken(true);
      } else {
        setApiToken('');
        setHasToken(false);
      }
      setSaveStatus('idle');
      setValidationMessage('');
    }
  }, [isOpen]);

  const handleSave = () => {
    setSaveStatus('idle');
    setValidationMessage('');

    const validation = validateApiToken(apiToken);
    
    if (!validation.valid) {
      setSaveStatus('error');
      setValidationMessage(validation.message || 'Invalid API token');
      return;
    }

    try {
      saveApiToken(apiToken);
      setSaveStatus('success');
      setHasToken(true);
      if (validation.message) {
        setValidationMessage(validation.message);
      } else {
        setValidationMessage('API token saved successfully!');
      }
      
      // Auto-close after 2 seconds on success
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      setSaveStatus('error');
      setValidationMessage('Failed to save API token. Please try again.');
    }
  };

  const handleClear = () => {
    if (window.confirm('Are you sure you want to clear your API token?')) {
      try {
        clearApiToken();
        setApiToken('');
        setHasToken(false);
        setSaveStatus('success');
        setValidationMessage('API token cleared successfully');
      } catch (error) {
        setSaveStatus('error');
        setValidationMessage('Failed to clear API token');
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && apiToken.trim()) {
      handleSave();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200]">
      <div className="bg-white border-2 border-black rounded-lg shadow-[8px_8px_0px_0px_rgba(0,0,0,0.2)] w-full max-w-2xl mx-4 animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-black/10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-md">
              <Key size={20} className="text-black" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-black">Settings</h2>
              <p className="text-xs text-gray-500 mt-0.5">Configure your Gemini API token</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-black transition-colors p-1 rounded-md hover:bg-gray-100"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex gap-3">
              <AlertCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-1">Getting your Gemini API Key</p>
                <p className="text-blue-700 mb-2">
                  You need a Gemini API key to use AI-powered features like architecture analysis and sketch recognition.
                </p>
                <a
                  href="https://aistudio.google.com/app/apikey"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium underline"
                >
                  Get your free API key from Google AI Studio
                  <ExternalLink size={14} />
                </a>
              </div>
            </div>
          </div>

          {/* Token Status */}
          {hasToken && (
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle size={16} className="text-green-600" />
              <span className="text-green-700 font-medium">API token is configured</span>
            </div>
          )}

          {/* API Token Input */}
          <div className="space-y-2">
            <label htmlFor="api-token" className="block text-sm font-medium text-black">
              Gemini API Token
            </label>
            <div className="relative">
              <input
                id="api-token"
                type={showToken ? 'text' : 'password'}
                value={apiToken}
                onChange={(e) => setApiToken(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Paste your Gemini API key here..."
                className="w-full px-4 py-3 pr-12 border-2 border-black/10 rounded-md focus:outline-none focus:border-black transition-colors text-sm font-mono"
              />
              <button
                type="button"
                onClick={() => setShowToken(!showToken)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black transition-colors"
                title={showToken ? 'Hide token' : 'Show token'}
              >
                {showToken ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <p className="text-xs text-gray-500">
              Your API token is stored locally in your browser and never sent anywhere except to Google's Gemini API.
            </p>
          </div>

          {/* Validation Message */}
          {validationMessage && (
            <div className={`flex items-center gap-2 p-3 rounded-md ${
              saveStatus === 'success' 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-orange-50 border border-orange-200'
            }`}>
              {saveStatus === 'success' ? (
                <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
              ) : (
                <AlertCircle size={16} className="text-orange-600 flex-shrink-0" />
              )}
              <span className={`text-sm ${
                saveStatus === 'success' ? 'text-green-700' : 'text-orange-700'
              }`}>
                {validationMessage}
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-black/10 bg-gray-50">
          <button
            onClick={handleClear}
            disabled={!hasToken}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Trash2 size={16} />
            Clear Token
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-black hover:bg-gray-100 rounded-md transition-colors border border-black/10"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!apiToken.trim()}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-black text-white rounded-md hover:bg-gray-800 transition-colors shadow-[3px_3px_0px_0px_rgba(0,0,0,0.2)] active:shadow-none active:translate-x-[1px] active:translate-y-[1px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save size={16} />
              Save Token
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
